import React, { Children } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { makeStyles } from '@mui/styles'

const useStyles = makeStyles(({ palette }) => ({
  markdown: {
    color: palette.text.secondary,
    '& img': {
      maxWidth: '100%',
      display: 'block',
      margin: 'auto',
    },
    '& h2': {
      position: 'relative',
      color: palette.text.primary,
      lineHeight: 1.4,
      paddingBottom: 8,
      marginTop: 12,
      marginBottom: 12,
      fontSize: 20,
      borderBottom: '1px solid rgba(0,0,0,0.15)',
      '&:after': {
        content: '""',
        display: 'block',
        position: 'absolute',
        bottom: -1,
        width: 120,
        height: 1,
        background: `${process.env.REACT_APP_COLOR_PRIMARY}`,
      },
    },
    '& h3': {
      color: palette.text.primary,
      lineHeight: 1.4,
      marginTop: 12,
      marginBottom: 12,
      fontSize: 18,
    },
    '& p': {
      marginBottom: 12,
    },
    '& a': {
      textDecoration: 'underline',
    },
    '& ul, ol': {
      paddingLeft: 28,
    },
    '& li > ol, li > ul': {
      paddingLeft: 12,
    },
    '& li': {
      marginBottom: 8,
    },
    '& del': {
      textDecoration: 'line-through',
    },
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      marginBottom: 16,
      fontSize: 14,
    },
    '& th, & td': {
      border: `1px solid ${palette.divider}`,
      padding: '8px 12px',
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: palette.grey[100],
      fontWeight: 600,
    },
    '& tr:nth-child(even) td': {
      backgroundColor: palette.grey[50],
    },
  },
}))

const createHeadingId = (children) => {
  const headingText = Children.toArray(children)
    .map(child => typeof child === 'string' ? child : '')
    .join('')
    .toLowerCase()
    .replace(/[^\w\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\u4e00-\u9faf\u3400-\u4dbf]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return headingText
}

export default function Markdown({ children, ...props }) {
  const classes = useStyles()
  return (
    <ReactMarkdown
      className={classes.markdown}
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children, ...props }) => {
          const id = createHeadingId(children)
          return <h1 id={id} {...props}>{children}</h1>
        },
        h2: ({ children, ...props }) => {
          const id = createHeadingId(children)
          return <h2 id={id} {...props}>{children}</h2>
        },
        h3: ({ children, ...props }) => {
          const id = createHeadingId(children)
          return <h3 id={id} {...props}>{children}</h3>
        },
        h4: ({ children, ...props }) => {
          const id = createHeadingId(children)
          return <h4 id={id} {...props}>{children}</h4>
        },
        h5: ({ children, ...props }) => {
          const id = createHeadingId(children)
          return <h5 id={id} {...props}>{children}</h5>
        },
        h6: ({ children, ...props }) => {
          const id = createHeadingId(children)
          return <h6 id={id} {...props}>{children}</h6>
        },
        p: ({ children, ...props }) => (
          <p {...props}>
            {Children.map(children, value => {
              if (value === '<br />') {
                return <div style={{ height: 24 }} />
              }
              return value
            })}
          </p>
        ),
        del: ({ children, ...props }) => (
          <del {...props} style={{ textDecoration: 'line-through' }}>
            {children}
          </del>
        ),
        table: ({ children, ...props }) => (
          <table {...props} style={{ 
            width: '100%', 
            borderCollapse: 'collapse', 
            marginBottom: 16, 
            fontSize: 14 
          }}>
            {children}
          </table>
        ),
        th: ({ children, ...props }) => (
          <th {...props} style={{ 
            border: '1px solid rgba(0,0,0,0.15)', 
            padding: '8px 12px', 
            textAlign: 'left',
            backgroundColor: '#f5f5f5',
            fontWeight: 600
          }}>
            {children}
          </th>
        ),
        td: ({ children, ...props }) => (
          <td {...props} style={{
            border: '1px solid rgba(0,0,0,0.15)',
            padding: '8px 12px',
            textAlign: 'left'
          }}>
            {children}
          </td>
        ),
        a: ({ href, children, ...props }) => {
          if (href && href.startsWith('#')) {
            const handleClick = (e) => {
              e.preventDefault()
              const elementId = decodeURIComponent(href.slice(1))
              const element = document.getElementById(elementId)
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
            }
            return (
              <a href={href} onClick={handleClick} {...props}>
                {children}
              </a>
            )
          }
          return (
            <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
              {children}
            </a>
          )
        },
      }}
      {...props}
    >
      {children}
    </ReactMarkdown>
  )
}
